import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import { ColumnsType } from 'antd/es/table';
import AppTable from 'components/AppTable';
import AppTableActionMenu from 'components/AppTableActionMenu';
import OverlayPanelWrapper from 'components/Overlays/OverlayPanelWrapper';
import PeakSeasonAddEditPanel from 'features/configuration/components/PeakSeasonAddEditPanel';
import {
  useDeletePeakMutation,
  useGetPeaksQuery,
} from 'features/configuration/query';
import {
  PEAK_SEASON_TYPE,
  TDeletePeakPayload,
  TPeakSeasonConfig,
} from 'features/configuration/types';
import { memo, useCallback, useMemo } from 'react';
import useHandleDeleteRecord from 'utils/hooks/useHandleDeleteRecord';
import { formatBeDateToAppDefaultDate } from 'utils/moment/utils';

const PeakSeasonConfiguration = () => {
  const {
    data: getPeaksResponse,
    isFetching,
    refetch,
  } = useGetPeaksQuery({
    type: PEAK_SEASON_TYPE.CUSTOMIZE,
  });

  const [deletePeakMutationFn, { isLoading }] = useDeletePeakMutation();

  const { onDeleteRecord } = useHandleDeleteRecord<TDeletePeakPayload>({
    actionLabel: 'Cao điểm',
    payloadDataKey: 'code',
    deleteRecordKey: 'code',
    deleteMutationFn: deletePeakMutationFn,
    isDeleting: isLoading,
    onDeletedSuccess: () => refetch(),
  });

  const renderDropdownItems = useCallback(
    (selectedPeakSeasonItem: TPeakSeasonConfig) => {
      return [
        {
          key: '1',
          label: (
            <OverlayPanelWrapper
              renderOverlayPanel={(open, setOpen) => (
                <PeakSeasonAddEditPanel
                  open={open}
                  setOpen={setOpen}
                  selectedPeakSeasonItem={selectedPeakSeasonItem}
                />
              )}
            >
              Chỉnh sửa
            </OverlayPanelWrapper>
          ),
        },
        {
          key: '2',
          label: (
            <span
              onClick={() => onDeleteRecord(selectedPeakSeasonItem)}
              className="text-red-500 hover:cursor-pointer"
            >
              Xóa
            </span>
          ),
        },
      ];
    },
    [onDeleteRecord]
  );

  const tableColumns = useMemo(
    (): ColumnsType<TPeakSeasonConfig> => [
      {
        title: 'Tên cao điểm',
        width: 180,
        render: (record: TPeakSeasonConfig) => (
          <OverlayPanelWrapper
            renderOverlayPanel={(open, setOpen) => (
              <PeakSeasonAddEditPanel
                open={open}
                setOpen={setOpen}
                selectedPeakSeasonItem={record}
              />
            )}
          >
            <span className="cursor-pointer">{record.name}</span>
          </OverlayPanelWrapper>
        ),
      },
      {
        title: 'Mô tả',
        width: 250,
        dataIndex: 'description',
        render: description => (
          <p className="line-clamp-2 whitespace-pre-line">{description}</p>
        ),
      },
      {
        title: 'Khoảng thời gian',
        width: 200,
        render: (record: TPeakSeasonConfig) => {
          return (
            <div className="flex items-center gap-x-1">
              <CalendarMonthIcon className="mb-0.5 text-lg text-slate-500" />
              <time>{formatBeDateToAppDefaultDate(record.startFrom)}</time>
              <span>-</span>
              <time>{formatBeDateToAppDefaultDate(record.endTo)}</time>
            </div>
          );
        },
      },
      {
        title: 'Tác vụ',
        fixed: 'right',
        width: 100,
        render: (record: TPeakSeasonConfig) => (
          <AppTableActionMenu items={renderDropdownItems(record)} />
        ),
      },
    ],
    [renderDropdownItems]
  );

  return (
    <AppTable
      rowKey={record => record.code}
      dataSource={getPeaksResponse?.data ?? []}
      columns={tableColumns}
      loading={isFetching}
      mode="simple"
      pagination={false}
    />
  );
};

export default memo(PeakSeasonConfiguration);
